/* global confetti, $ */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeAPI } from "../../services/api";

const MODAL_ID = "#modal-daily-bonus";
const REWARD_CARDS = [10, 15, 20, 25, 30, 40, 50];

const readUserFromStorage = () => {
  try {
    const rawUser = localStorage.getItem("user");
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
};

export default function DailyBonusModal() {
  const [user, setUser] = useState(() => readUserFromStorage());
  const [isClaiming, setIsClaiming] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const userId = user?.user_id;
  const canClaimReward = user?.is_reward_claimed === false;
  const currentStreak = Math.max(Number(user?.streak_count) || 0, 0);
  const displayStreak = Math.min(Math.max(currentStreak, 1), 7);

  const previousBalance = useMemo(
    () => Number(user?.coin_balance ?? user?.platform_coin_balance ?? user?.coins ?? 0),
    [user]
  );

  const syncUser = useCallback(() => {
    setUser(readUserFromStorage());
  }, []);

  const showModal = useCallback(() => {
    if (!window.UIkit || typeof window.UIkit.modal !== "function") return;
    const modalElement = document.querySelector(MODAL_ID);
    if (!modalElement) return;
    const modalInstance = window.UIkit.modal(modalElement, { container: false });
    if (modalInstance && typeof modalInstance.show === "function") {
      modalInstance.show();
    }
  }, []);

  const hideModal = useCallback(() => {
    if (!window.UIkit || typeof window.UIkit.modal !== "function") return;
    const modalElement = document.querySelector(MODAL_ID);
    if (!modalElement) return;
    const modalInstance = window.UIkit.modal(modalElement, { container: false });
    if (modalInstance && typeof modalInstance.hide === "function") {
      modalInstance.hide();
    }
  }, []);

  const coinEffect = useCallback((currentBalance, lastBalance) => {
    if (!Number.isFinite(currentBalance) || !Number.isFinite(lastBalance)) return;
    if (currentBalance === lastBalance) return;
    if (typeof $ !== "function") return;

    const coinTarget = $(".coin-balance");
    if (!coinTarget?.length || typeof coinTarget.animateNumber !== "function") return;
    coinTarget.prop("number", lastBalance).animateNumber({ number: currentBalance }, 500);
  }, []);

  const launchConfetti = useCallback(() => {
    if (typeof confetti !== "function") return;
    const colors = ["#ff9800", "#ffffff"];
    confetti({
      particleCount: 100,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors,
    });
    confetti({
      particleCount: 100,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors,
    });
  }, []);

  const refreshUserData = useCallback(async () => {
    const token = localStorage.getItem("token");
    const localUser = readUserFromStorage();
    if (!token || !localUser?.email) {
      setUser(localUser);
      return localUser;
    }

    const updatedUser = await makeAPI("/api/get_user_data", "POST", token, { email: localUser.email });
    if (updatedUser?.status === "S" && updatedUser?.data) {
      localStorage.setItem("user", JSON.stringify(updatedUser.data));
      setUser(updatedUser.data);
      window.dispatchEvent(new CustomEvent("auth:updated"));
      return updatedUser.data;
    }

    const fallbackUser = readUserFromStorage();
    setUser(fallbackUser);
    return fallbackUser;
  }, []);

  const claimDailyBonus = useCallback(async () => {
    if (!userId || isClaiming || !canClaimReward) return;

    setIsClaiming(true);
    setErrorMsg("");

    try {
      const response = await makeAPI("/api/daily_reward_claim", "POST", true, { user_id: Number(userId) });
      if (response?.status !== "S") {
        setErrorMsg(response?.response?.message || response?.message || "Unable to claim bonus.");
        return;
      }

      launchConfetti();
      const latestUser = await refreshUserData();
      const currentBalance = Number(latestUser?.coin_balance ?? latestUser?.platform_coin_balance ?? latestUser?.coins ?? 0);
      coinEffect(currentBalance, previousBalance);
      hideModal();
    } catch (error) {
      setErrorMsg(error?.message || "Something went wrong while claiming bonus.");
    } finally {
      setIsClaiming(false);
    }
  }, [canClaimReward, coinEffect, hideModal, isClaiming, launchConfetti, previousBalance, refreshUserData, userId]);

  useEffect(() => {
    syncUser();
    window.addEventListener("auth:updated", syncUser);
    window.addEventListener("storage", syncUser);
    return () => {
      window.removeEventListener("auth:updated", syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, [syncUser]);

  useEffect(() => {
    if (userId && canClaimReward) {
      showModal();
    } else {
      hideModal();
    }
  }, [canClaimReward, hideModal, showModal, userId]);

  useEffect(() => {
    return () => {
      hideModal();
    };
  }, [hideModal]);

  if (!userId) return null;

  return (
    <div className="uk-modal uk-flex-top db-modal-wrap" id="modal-daily-bonus" data-uk-modal="container: false; bg-close: false; esc-close: false;">
      <style>{`
        .db-modal-wrap {
          display: none;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 10px;
        }
        .db-modal-wrap.uk-open {
          display: flex !important;
        }
        .db-modal-wrap .uk-modal-dialog {
          margin: 0 auto;
          transform: translateY(18px) scale(0.98);
          opacity: 0;
          transition: transform .35s ease, opacity .35s ease;
        }
        .db-modal-wrap .uk-modal-dialog.db-shell {
          width: min(92vw, 520px) !important;
          max-width: 520px !important;
        }
        .db-modal-wrap.uk-open .uk-modal-dialog {
          transform: translateY(0) scale(1);
          opacity: 1;
        }
        .db-shell {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(100% 100% at 0% 0%, rgba(255,129,14,.22), transparent 55%),
            radial-gradient(100% 100% at 100% 100%, rgba(255,129,14,.10), transparent 55%),
            linear-gradient(160deg, #161616 0%, #101010 100%);
        }
        .db-shell::before {
          content: "";
          position: absolute;
          inset: -30%;
          background: repeating-linear-gradient(
            115deg,
            rgba(255,255,255,.02) 0px,
            rgba(255,255,255,.02) 1px,
            transparent 1px,
            transparent 20px
          );
          animation: dbGridShift 12s linear infinite;
          pointer-events: none;
        }
        .db-header {
          position: relative;
          z-index: 1;
          text-align: center;
          margin-bottom: 18px;
        }
        .db-title {
          margin: 0;
          font-size: clamp(24px, 3.8vw, 34px);
          font-weight: 900;
          letter-spacing: .04em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255,129,14,.55), 0 0 24px rgba(255,129,14,.2);
        }
        .db-subtitle {
          margin: 8px 0 0;
          color: #d2d2d2;
          font-size: 13px;
          letter-spacing: .03em;
        }
        .db-divider {
          width: 72px;
          height: 6px;
          margin: 12px auto 0;
          border-radius: 99px;
          background: repeating-linear-gradient(
            -45deg,
            #ff810e 0 6px,
            rgba(255,129,14,.25) 6px 10px
          );
          box-shadow: 0 0 12px rgba(255,129,14,.45);
        }
        .db-grid {
          position: relative;
          z-index: 1;
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }
        .db-row {
          display: grid;
          gap: 12px;
          justify-content: center;
        }
        .db-row.top { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        .db-row.bottom { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .db-day-card {
          position: relative;
          width: 116px;
          animation: dbCardIn .45s ease both;
          animation-delay: var(--delay, 0ms);
        }
        .db-badge {
          position: absolute;
          top: -24px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2;
          display: inline-block;
          padding: 3px 10px;
          border-radius: 999px;
          font-size: 11px;
          letter-spacing: .08em;
          text-transform: uppercase;
          background: rgba(255,129,14,.18);
          color: #ffb36f;
          border: 1px solid rgba(255,129,14,.45);
        }
        .db-claim-wrap {
          position: relative;
          z-index: 1;
          margin-top: 24px;
          text-align: center;
        }
        .db-claim-btn {
          position: relative;
          min-width: 180px;
          border: 0;
          border-radius: 999px;
          padding: 10px 24px;
          font-weight: 800;
          letter-spacing: .07em;
          text-transform: uppercase;
          color: #fff;
          background: linear-gradient(120deg, #ff810e, #ff9b36, #ff810e);
          background-size: 220% 220%;
          box-shadow: 0 8px 30px rgba(255,129,14,.35);
          animation: dbButtonFlow 3.5s ease infinite;
          transition: transform .2s ease, box-shadow .2s ease;
        }
        .db-claim-btn:hover:enabled {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 14px 34px rgba(255,129,14,.45);
        }
        .db-claim-btn:disabled {
          opacity: .65;
          cursor: not-allowed;
          transform: none;
          box-shadow: 0 6px 18px rgba(0,0,0,.35);
        }
        .db-streak {
          margin-top: 10px;
          color: #e6e6e6;
          font-size: 14px;
        }
        .db-streak strong {
          color: #ffb36f;
        }
        .db-row.top {
          margin-top: 24px;
        }
        @media (max-width: 980px) {
          .db-row.top { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .db-row.bottom { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .db-day-card { width: 155px; }
        }
        @media (max-width: 767px) {
          .db-modal-wrap .uk-modal-dialog.db-shell {
            width: min(94vw, 420px) !important;
            max-width: 420px !important;
          }
          .db-grid { gap: 10px; }
          .db-row.top, .db-row.bottom {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
          }
          .db-day-card { width: 140px; }
        }
        @media (max-width: 420px) {
          .db-modal-wrap .uk-modal-dialog.db-shell {
            width: calc(100vw - 12px) !important;
            max-width: calc(100vw - 12px) !important;
          }
          .db-day-card { width: 132px; }
          .db-claim-btn { min-width: 180px; }
        }
        @keyframes dbCardIn {
          from { opacity: 0; transform: translateY(8px) scale(.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes dbGridShift {
          from { transform: translate3d(0,0,0); }
          to { transform: translate3d(120px, -80px, 0); }
        }
        @keyframes dbButtonFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <div className="uk-modal-dialog uk-modal-body uk-margin-auto-vertical db-shell" style={styles.modalContent}>
        <button className="uk-modal-close-default" type="button" data-uk-close></button>
        <div className="db-header">
          <h2 className="db-title">Daily Bonus</h2>
          <div className="db-divider"></div>
          <p className="db-subtitle">Log in every day, build streaks, and stack bigger coin rewards.</p>
        </div>

        {errorMsg ? (
          <div className="uk-alert-danger uk-margin-small" data-uk-alert>
            {errorMsg}
          </div>
        ) : null}

        <div className="coin-value db-grid">
          <div className="db-row top">
            {REWARD_CARDS.slice(0, 4).map((coins, index) => (
              <DayCard
                key={index}
                day={index + 1}
                coins={coins}
                done={index + 1 < displayStreak}
                today={index + 1 === displayStreak}
                delay={index}
              />
            ))}
          </div>
          <div className="db-row bottom">
            {REWARD_CARDS.slice(4).map((coins, i) => {
              const index = i + 4;
              return (
                <DayCard
                  key={index}
                  day={index + 1}
                  coins={coins}
                  done={index + 1 < displayStreak}
                  today={index + 1 === displayStreak}
                  delay={index}
                />
              );
            })}
          </div>
        </div>

        <div className="db-claim-wrap">
          <button
            className="db-claim-btn"
            onClick={claimDailyBonus}
            disabled={isClaiming || !canClaimReward}
          >
            {isClaiming ? "Claiming..." : "Claim Bonus"}
          </button>
          <p className="db-streak">
            Current Streak: <strong>Day {currentStreak}</strong>
          </p>
        </div>
      </div>
    </div>
  );
}

const DayCard = ({ day, coins, today, done, delay }) => {
  const cardStyle = {
    ...styles.rewardCard,
    ...(done ? styles.done : {}),
    ...(today ? styles.today : {}),
  };

  return (
    <div className="db-day-card" style={{ "--delay": `${delay * 70}ms` }}>
      {today ? <span className="db-badge">Today</span> : null}
      <div style={cardStyle}>
        <div style={styles.rewardDay}>Day {day}{day === 7 ? "/+" : ""}</div>
        <div style={styles.rewardAmount}>
          +{coins} Coins {done ? <i className="fa-solid fa-circle-check" style={{ color: "#ff810e" }}></i> : null}
        </div>
      </div>
    </div>
  );
};

const styles = {
  modalContent: {
    border: "1px solid #FF810E",
    borderRadius: "18px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.55), 0 0 30px rgba(255, 129, 14, 0.2)",
    color: "#fff",
    maxWidth: "520px",
    width: "calc(100% - 18px)",
    padding: "14px",
  },
  rewardCard: {
    background: "linear-gradient(180deg, rgba(255,129,14,.08), rgba(255,255,255,.01))",
    border: "1px solid rgba(255,129,14,.18)",
    borderRadius: "12px",
    padding: "9px 7px",
    textAlign: "center",
    minHeight: "74px",
  },
  today: {
    border: "1px solid rgba(255,129,14,.7)",
    background: "linear-gradient(165deg, rgba(255,129,14,.3), rgba(255,129,14,.12))",
    boxShadow: "inset 0 0 20px rgba(255,129,14,.08), 0 0 14px rgba(255,129,14,.26)",
  },
  done: {
    border: "1px solid rgba(255,129,14,.45)",
    background: "linear-gradient(180deg, rgba(255,129,14,.20), rgba(255,129,14,.08))",
  },
  rewardDay: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: ".06em",
    color: "#c9c9c9",
  },
  rewardAmount: {
    marginTop: "4px",
    fontSize: "16px",
    fontWeight: "800",
    letterSpacing: ".02em",
    color: "#ffad56",
  },
};
